/**
 * Pack lots, release and lab (F7.4–F7.6, F7-B1.a) console API (uc-core src/modules/pack + lab):
 *   /pack-lots GET ?status&hold&skuId&packedFrom&packedTo&search · /pack-lots/:idOrCode
 *   /pack-lots/:id/dispatches · /:id/release-checklist · POST /:id/decisions · POST /:id/delabel-runs
 *   /pack-lots/:id/carton-labels.pdf · /:id/coder-sheet.pdf
 *   /release-queue ?state=WAITING|HELD|RELEASED|ALL&skuId&search · /release-decisions ?packLotId
 *   /lab-reports GET ?status&overall&unitId&sampleId&labOrgId&search · POST (multipart) · /:id · POST /:id/verify
 *   /lab-limits GET ?cropId&panel&search · POST · DELETE /:id
 *   /units/:id/holds · /skus
 */
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api'
import { getList, getOne, getPage, toQuery, useConsoleMutation, usePageQuery, type Page, type PageParams } from './core'
import { arr, bool, inrToPaise, iso, kgToGrams, num, numOrNull, obj, str, strOrNull } from './mappers'
import type { ExceptionLiteRow } from '../components/ExceptionTable'

/** Query keys for this area. Invalidate `lk.all` after any signature. */
export const lk = {
  all: ['console', 'lab'] as const,
  packLots: ['console', 'lab', 'pack-lots'] as const,
  checklist: ['console', 'lab', 'checklist'] as const,
  decisions: ['console', 'lab', 'decisions'] as const,
  queue: ['console', 'lab', 'release-queue'] as const,
  holds: ['console', 'lab', 'holds'] as const,
  reports: ['console', 'lab', 'reports'] as const,
  limits: ['console', 'lab', 'limits'] as const,
  skus: ['console', 'lab', 'skus'] as const,
}

// ============================== pack lots ==============================

export interface PackLotRow {
  id: string
  code: string
  status: string
  hold: string
  qrUrl: string | null
  packDate: string | null
  bestBefore: string | null
  pouchCount: number | null
  labelsPrinted: number | null
  labelsApplied: number | null
  labelsSpoiled: number | null
  labelsDestroyed: number | null
  unitId: string
  runId: string | null
  runCode: string | null
  runStatus: string | null
  /** The pack run's operator and cleanout person: neither may sign the release. */
  operatorId: string | null
  cleanoutById: string | null
  skuId: string | null
  skuCode: string | null
  skuName: string | null
  netG: number | null
  pouchesPerCarton: number | null
  labelVersionId: string | null
  labelCode: string | null
  mrpPaise: number | null
  allergenStatement: string | null
  batchUnitId: string | null
  batchCode: string | null
  packedG: number | null
  pouchesDispatched: number
  createdAt: string | null
}

export const mapPackLotRow = (r: Record<string, unknown>): PackLotRow => ({
  id: str(r.id),
  code: str(r.code),
  status: str(r.status),
  hold: str(r.hold, 'NONE'),
  qrUrl: strOrNull(r.qr_url),
  packDate: strOrNull(r.pack_date),
  bestBefore: strOrNull(r.best_before),
  pouchCount: numOrNull(r.pouch_count),
  labelsPrinted: numOrNull(r.labels_printed),
  labelsApplied: numOrNull(r.labels_applied),
  labelsSpoiled: numOrNull(r.labels_spoiled),
  labelsDestroyed: numOrNull(r.labels_destroyed),
  unitId: str(r.unit_id),
  runId: strOrNull(r.run_id),
  runCode: strOrNull(r.run_code),
  runStatus: strOrNull(r.run_status),
  operatorId: strOrNull(r.operator_id),
  cleanoutById: strOrNull(r.cleanout_by_id),
  skuId: strOrNull(r.sku_id),
  skuCode: strOrNull(r.sku_code),
  skuName: strOrNull(r.sku_name),
  netG: numOrNull(r.net_g),
  pouchesPerCarton: numOrNull(r.pouches_per_carton),
  labelVersionId: strOrNull(r.label_version_id),
  labelCode: strOrNull(r.label_code),
  mrpPaise: inrToPaise(r.mrp_inr as string),
  allergenStatement: strOrNull(r.allergen_statement),
  batchUnitId: strOrNull(r.batch_unit_id),
  batchCode: strOrNull(r.batch_code),
  packedG: kgToGrams(r.packed_kg as string),
  pouchesDispatched: num(r.pouches_dispatched),
  createdAt: iso(r.created_at),
})

export interface LabStatus {
  overall: string
  requiredPanels: string[]
  coveredPanels: string[]
  reportIds: string[]
  latestTestedAt: string | null
  labUnitId: string | null
}
export const mapLabStatus = (r: Record<string, unknown>): LabStatus => ({
  overall: str(r.overall, 'PENDING'),
  requiredPanels: arr<string>(r.required_panels).map(String),
  coveredPanels: arr<string>(r.covered_panels).map(String),
  reportIds: arr<string>(r.report_ids).map(String),
  latestTestedAt: strOrNull(r.latest_tested_at),
  labUnitId: strOrNull(r.lab_unit_id),
})

export interface RunBalance {
  status: string | null
  inputG: number | null
  outputG: number | null
  productG: number | null
  residualG: number | null
  residualPct: string | null
  yieldPct: string | null
  lossLimitPct: string | null
}
const mapBalance = (r: Record<string, unknown>): RunBalance => ({
  status: strOrNull(r.status),
  inputG: kgToGrams(r.input_kg as string),
  outputG: kgToGrams(r.output_kg as string),
  productG: kgToGrams(r.product_kg as string),
  residualG: kgToGrams(r.residual_kg as string),
  residualPct: strOrNull(r.residual_pct),
  yieldPct: strOrNull(r.yield_pct),
  lossLimitPct: strOrNull(r.loss_limit_pct),
})

export interface PouchCheck {
  at: string | null
  position: string
  match: boolean
  scannedText: string | null
  photoId: string | null
}
export interface LabelsReconciled {
  at: string | null
  printed: number | null
  applied: number | null
  spoiled: number | null
  destroyed: number | null
  gap: number | null
  pouchesRejected: number | null
  lastPouchLabelOk: boolean | null
  counterPhotoId: string | null
}

export interface PackLotDetail extends PackLotRow {
  pouchesLeft: number
  reconciliation: { ok: boolean; gap: number | null }
  sellable: boolean
  labStatus: LabStatus | null
  balance: RunBalance | null
  pouchChecks: PouchCheck[]
  labelsReconciled: LabelsReconciled | null
  exceptions: ExceptionLiteRow[]
}

export const mapPackLotDetail = (r: Record<string, unknown>): PackLotDetail => {
  const checks = arr(r.checks).map(obj)
  const pouchChecks = checks
    .filter((c) => c.biz_step === 'POUCH_VERIFIED')
    .map((c): PouchCheck => {
      const p = obj(c.payload)
      return {
        at: iso(c.occurred_at),
        position: str(p.position),
        match: bool(p.match),
        scannedText: strOrNull(p.scanned_text),
        photoId: strOrNull(p.photo_id),
      }
    })
  const rec = checks.filter((c) => c.biz_step === 'LABELS_RECONCILED').pop()
  const rp = obj(rec?.payload)
  const recon = obj(r.reconciliation)
  return {
    ...mapPackLotRow(r),
    pouchesLeft: num(r.pouches_left),
    reconciliation: { ok: bool(recon.ok), gap: numOrNull(recon.gap) },
    sellable: bool(r.sellable),
    labStatus: r.lab_status ? mapLabStatus(obj(r.lab_status)) : null,
    balance: r.balance ? mapBalance(obj(r.balance)) : null,
    pouchChecks,
    labelsReconciled: rec
      ? {
          at: iso(rec.occurred_at),
          printed: numOrNull(rp.printed),
          applied: numOrNull(rp.applied),
          spoiled: numOrNull(rp.spoiled),
          destroyed: numOrNull(rp.destroyed),
          gap: numOrNull(rp.gap),
          pouchesRejected: numOrNull(rp.pouches_rejected),
          lastPouchLabelOk: rp.last_pouch_label_ok === undefined ? null : bool(rp.last_pouch_label_ok),
          counterPhotoId: strOrNull(rp.counter_photo_id),
        }
      : null,
    exceptions: arr(r.exceptions)
      .map(obj)
      .map((e) => ({
        id: str(e.id),
        type: str(e.type),
        severity: str(e.severity, 'LOW'),
        status: str(e.status),
        subjectType: str(e.subject_type),
        raisedAt: iso(e.raised_at),
        resolutionCode: strOrNull(e.resolution_code),
      })),
  }
}

/** The latest scan at each position (FIRST, LAST) and how many scans before it did not match. */
export function latestPouchChecks(checks: PouchCheck[]): Array<PouchCheck & { misses: number }> {
  const out: Array<PouchCheck & { misses: number }> = []
  for (const pos of ['FIRST', 'LAST']) {
    const at = checks.filter((c) => c.position === pos)
    const last = at[at.length - 1]
    if (last) out.push({ ...last, misses: at.filter((c) => !c.match).length - (last.match ? 0 : 1) })
  }
  return out
}

export interface PackLotFilters extends PageParams {
  status?: string
  hold?: string
  skuId?: string
  packedFrom?: string
  packedTo?: string
  search?: string
}
export function usePackLots(f: PackLotFilters) {
  return usePageQuery([...lk.packLots, 'list', f], () => getPage('/pack-lots', f, mapPackLotRow))
}
export function usePackLot(idOrCode: string | null | undefined) {
  return useQuery({
    queryKey: [...lk.packLots, 'one', idOrCode],
    enabled: Boolean(idOrCode),
    queryFn: () => getOne(`/pack-lots/${encodeURIComponent(idOrCode!)}`, mapPackLotDetail),
  })
}

export interface PackLotDispatch {
  shipmentId: string
  shipmentCode: string
  toOrgId: string | null
  toOrg: string | null
  challanNo: string | null
  pouches: number
  dispatchedAt: string | null
  vehicleNo: string | null
}
export const mapPackLotDispatch = (r: Record<string, unknown>): PackLotDispatch => ({
  shipmentId: str(r.shipment_id),
  shipmentCode: str(r.shipment_code),
  toOrgId: strOrNull(r.to_org_id),
  toOrg: strOrNull(r.to_org),
  challanNo: strOrNull(r.challan_no),
  pouches: num(r.pouches),
  dispatchedAt: iso(r.dispatched_at),
  vehicleNo: strOrNull(r.vehicle_no),
})
export function usePackLotDispatches(id: string | null | undefined) {
  return useQuery({
    queryKey: [...lk.packLots, 'dispatches', id],
    enabled: Boolean(id),
    queryFn: () => getList(`/pack-lots/${id}/dispatches`, undefined, mapPackLotDispatch),
  })
}

// ============================== release checklist & decisions ==============================

export interface ChecklistEvidence {
  type: string
  id: string
  code: string | null
}
export interface ChecklistItem {
  key: string
  ok: boolean
  detail: string[]
  evidence: ChecklistEvidence[]
}
export interface Checklist {
  packLotId: string
  items: ChecklistItem[]
  canRelease: boolean
  labReportIds: string[]
  computedAt: string | null
}
export const mapChecklist = (r: Record<string, unknown>): Checklist => ({
  packLotId: str(r.pack_lot_id),
  items: arr(r.items)
    .map(obj)
    .map((i) => ({
      key: str(i.key),
      ok: bool(i.ok),
      detail: arr<unknown>(i.detail).map(String),
      evidence: arr(i.evidence)
        .map(obj)
        .map((e) => ({ type: str(e.type), id: str(e.id), code: strOrNull(e.code) })),
    })),
  canRelease: bool(r.can_release),
  labReportIds: arr<unknown>(r.lab_report_ids).map(String),
  computedAt: iso(r.computed_at),
})
export function useReleaseChecklist(packLotId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: [...lk.checklist, packLotId],
    enabled: Boolean(packLotId) && enabled,
    queryFn: () => getOne(`/pack-lots/${packLotId}/release-checklist`, mapChecklist),
  })
}

export interface DecisionRow {
  id: string
  decision: string
  auto: boolean
  reason: string | null
  qtyG: number | null
  decidedAt: string | null
  decidedById: string | null
  decidedByName: string | null
  packLotId: string | null
  packLotCode: string | null
  unitCode: string | null
}
export const mapDecision = (r: Record<string, unknown>): DecisionRow => {
  const by = obj(r.decided_by)
  return {
    id: str(r.id),
    decision: str(r.decision),
    auto: bool(r.auto),
    reason: strOrNull(r.reason),
    qtyG: kgToGrams(r.qty_kg as string),
    decidedAt: iso(r.decided_at),
    decidedById: strOrNull(by.id ?? r.decided_by_id),
    decidedByName: strOrNull(by.name) ?? strOrNull(by.username),
    packLotId: strOrNull(r.pack_lot_id),
    packLotCode: strOrNull(r.pack_lot_code),
    unitCode: strOrNull(r.unit_code),
  }
}
export function useReleaseDecisions(f: PageParams & { packLotId?: string; unitId?: string }, enabled = true) {
  return usePageQuery([...lk.decisions, f], () => getPage('/release-decisions', f, mapDecision), { enabled })
}

export interface ReleaseQueueRow {
  id: string
  code: string
  status: string
  hold: string
  packDate: string | null
  bestBefore: string | null
  pouchCount: number | null
  skuCode: string | null
  skuName: string | null
  latest: { id: string; decision: string; reason: string | null; auto: boolean; decidedAt: string | null; decidedById: string | null } | null
}
export const mapReleaseQueueRow = (r: Record<string, unknown>): ReleaseQueueRow => {
  const d = r.latest_decision ? obj(r.latest_decision) : null
  return {
    id: str(r.id),
    code: str(r.code),
    status: str(r.status),
    hold: str(r.hold, 'NONE'),
    packDate: strOrNull(r.pack_date),
    bestBefore: strOrNull(r.best_before),
    pouchCount: numOrNull(r.pouch_count),
    skuCode: strOrNull(r.sku_code),
    skuName: strOrNull(r.sku_name),
    latest: d
      ? {
          id: str(d.id),
          decision: str(d.decision),
          reason: strOrNull(d.reason),
          auto: bool(d.auto),
          decidedAt: iso(d.decided_at),
          decidedById: strOrNull(d.decided_by_id),
        }
      : null,
  }
}
export type QueueState = 'WAITING' | 'HELD' | 'RELEASED' | 'ALL'
export async function fetchReleaseQueue(f: PageParams & { state: QueueState; skuId?: string; search?: string }): Promise<Page<ReleaseQueueRow>> {
  // 'ALL' is a real value here, but toQuery drops 'ALL' (and the server default is WAITING): send it as is.
  const { data, meta } = await api.paginated<Record<string, unknown>>('/release-queue', {
    query: { ...toQuery({ ...f, state: undefined }), state: f.state },
  })
  return {
    rows: data.map((r) => mapReleaseQueueRow(obj(r))),
    total: meta.total,
    page: meta.page,
    limit: meta.limit,
    totalPages: meta.totalPages,
  }
}
export function useReleaseQueue(f: PageParams & { state: QueueState; skuId?: string; search?: string }) {
  return usePageQuery([...lk.queue, f], () => fetchReleaseQueue(f))
}

export interface DecideVars {
  packLotId: string
  body: { decision: 'RELEASE' | 'HOLD'; reason?: string; attested: true; reauthToken: string }
}
/** Errors are handled in the dialog (inline), never toasted. */
export function useDecideRelease() {
  return useConsoleMutation<DecideVars, Record<string, unknown>>({
    mutationFn: ({ packLotId, body }) => api.post(`/pack-lots/${packLotId}/decisions`, body),
    invalidate: [lk.all],
    success: (_d, v) => (v.body.decision === 'RELEASE' ? 'Pack lot released' : 'Pack lot put on hold'),
    onError: () => true,
  })
}

export function useOpenDelabelRun() {
  return useConsoleMutation<{ packLotId: string; body: { operatorId: string; locationId: string; equipmentId?: string; inputKg: string } }, Record<string, unknown>>({
    mutationFn: ({ packLotId, body }) => api.post(`/pack-lots/${packLotId}/delabel-runs`, body),
    invalidate: [lk.all],
    success: (d) => `De-label run ${str(obj(d).code, '')} opened — record its outputs on the phone`,
    onError: () => true,
  })
}

// ============================== holds ==============================

export interface HeldUnit {
  unitId: string
  code: string
  unitType: string
  hold: string
  holdReason: string | null
  scope: string
  labReportId: string | null
  packLotId: string | null
  packLotCode: string | null
  packLotStatus: string | null
  packLotHold: string | null
  pouchCount: number | null
  locationCode: string | null
}
export interface UnitHolds {
  held: HeldUnit[]
  exceptionId: string | null
}
export const mapUnitHolds = (r: Record<string, unknown>): UnitHolds => ({
  held: arr(r.held)
    .map(obj)
    .map((h) => ({
      unitId: str(h.unit_id),
      code: str(h.code),
      unitType: str(h.unit_type),
      hold: str(h.hold, 'NONE'),
      holdReason: strOrNull(h.hold_reason),
      scope: str(h.scope),
      labReportId: strOrNull(h.lab_report_id),
      packLotId: strOrNull(h.pack_lot_id),
      packLotCode: strOrNull(h.pack_lot_code),
      packLotStatus: strOrNull(h.pack_lot_status),
      packLotHold: strOrNull(h.pack_lot_hold),
      pouchCount: numOrNull(h.pouch_count),
      locationCode: strOrNull(h.location_code),
    })),
  exceptionId: strOrNull(r.exception_id),
})
export function useUnitHolds(unitId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: [...lk.holds, unitId],
    enabled: Boolean(unitId) && enabled,
    queryFn: () => getOne(`/units/${unitId}/holds`, mapUnitHolds),
  })
}

// ============================== lab reports ==============================

export interface LabReportRow {
  id: string
  reportNo: string
  status: string
  overall: string | null
  testedAt: string | null
  receivedAt: string | null
  unitId: string
  unitCode: string | null
  unitType: string | null
  sampleKind: string | null
  labOrgId: string | null
  labName: string | null
  enteredById: string | null
  verifiedById: string | null
  verifiedAt: string | null
  supersedesId: string | null
  rowCount: number
  rowsNotPass: number
}
export const mapLabReportRow = (r: Record<string, unknown>): LabReportRow => ({
  id: str(r.id),
  reportNo: str(r.report_no),
  status: str(r.status),
  overall: strOrNull(r.overall),
  testedAt: strOrNull(r.tested_at),
  receivedAt: iso(r.received_at),
  unitId: str(r.unit_id),
  unitCode: strOrNull(r.unit_code),
  unitType: strOrNull(r.unit_type),
  sampleKind: strOrNull(r.sample_kind),
  labOrgId: strOrNull(r.lab_org_id),
  labName: strOrNull(r.lab_name),
  enteredById: strOrNull(r.entered_by_id),
  verifiedById: strOrNull(r.verified_by_id),
  verifiedAt: iso(r.verified_at),
  supersedesId: strOrNull(r.supersedes_id),
  rowCount: Array.isArray(r.rows) ? r.rows.length : num(r.rows),
  rowsNotPass: Array.isArray(r.rows) ? arr(r.rows).filter((x) => obj(x).result_flag !== 'PASS').length : num(r.rows_not_pass),
})

export interface LabResultRow {
  id: string
  panel: string
  analyte: string
  value: string | null
  uom: string
  loq: string | null
  limitValue: string | null
  method: string | null
  flag: string
  text: string
}
export const mapLabResult = (r: Record<string, unknown>): LabResultRow => ({
  id: str(r.id, `${str(r.panel)}|${str(r.analyte)}`),
  panel: str(r.panel),
  analyte: str(r.analyte),
  value: strOrNull(r.value),
  uom: str(r.uom),
  loq: strOrNull(r.loq),
  limitValue: strOrNull(r.limit_value),
  method: strOrNull(r.method),
  flag: str(r.result_flag),
  text: str(r.result_text),
})

export interface LabReportDetail extends LabReportRow {
  sampleId: string | null
  sealPayload: string | null
  accreditationNo: string | null
  accreditationValidTo: string | null
  sha256: string | null
  supersededById: string | null
  pdfUrl: string | null
  results: LabResultRow[]
}
export const mapLabReportDetail = (r: Record<string, unknown>): LabReportDetail => ({
  ...mapLabReportRow(r),
  sampleId: strOrNull(r.sample_id),
  sealPayload: strOrNull(r.seal_payload),
  accreditationNo: strOrNull(r.accreditation_no),
  accreditationValidTo: strOrNull(r.accreditation_valid_to),
  sha256: strOrNull(r.report_sha256),
  supersededById: strOrNull(r.superseded_by_id),
  pdfUrl: strOrNull(r.pdf_url),
  results: arr(r.rows).map(obj).map(mapLabResult),
})

export interface LabReportFilters extends PageParams {
  status?: string
  overall?: string
  unitId?: string
  sampleId?: string
  labOrgId?: string
  search?: string
}
export function useLabReports(f: LabReportFilters, enabled = true) {
  return usePageQuery([...lk.reports, 'list', f], () => getPage('/lab-reports', f, mapLabReportRow), { enabled })
}
export function useLabReport(id: string | null | undefined) {
  return useQuery({
    queryKey: [...lk.reports, 'one', id],
    enabled: Boolean(id),
    // pdf_url is signed for an hour: read again well before it runs out.
    staleTime: 20 * 60_000,
    refetchInterval: 40 * 60_000,
    queryFn: () => getOne(`/lab-reports/${id}`, mapLabReportDetail),
  })
}

export interface LabReportSaved {
  id: string
  status: string
  overall: string | null
  unitId: string | null
  supersedesId: string | null
  warnings: string[]
  results: LabResultRow[]
  heldUnitIds: string[]
  exceptionId: string | null
}
export const mapLabReportSaved = (r: Record<string, unknown>): LabReportSaved => ({
  id: str(r.id),
  status: str(r.status),
  overall: strOrNull(r.overall),
  unitId: strOrNull(r.unit_id),
  supersedesId: strOrNull(r.supersedes_id),
  warnings: arr<unknown>(r.warnings).map(String),
  results: arr(r.rows).map(obj).map(mapLabResult),
  heldUnitIds: arr<unknown>(r.held_unit_ids).map(String),
  exceptionId: strOrNull(r.exception_id),
})

/** Multipart POST /lab-reports. Errors stay in the dialog. */
export function useUploadLabReport() {
  return useConsoleMutation<FormData, LabReportSaved>({
    mutationFn: async (fd) => mapLabReportSaved(obj(await api.post('/lab-reports', fd, { timeoutMs: 120_000 }))),
    invalidate: [lk.all],
    success: (d) => (d.heldUnitIds.length ? 'Report saved — stock put on hold' : 'Report saved'),
    onError: () => true,
  })
}

export function useVerifyLabReport() {
  return useConsoleMutation<{ id: string; decision: 'VERIFIED' | 'REJECTED'; note?: string }>({
    mutationFn: ({ id, decision, note }) => api.post(`/lab-reports/${id}/verify`, { decision, ...(note ? { note } : {}) }),
    invalidate: [lk.all],
    success: (_d, v) => (v.decision === 'VERIFIED' ? 'Report verified' : 'Report rejected'),
    onError: () => true,
  })
}

// ============================== lab limits ==============================

export interface LabLimitRow {
  id: string
  cropId: string | null
  cropName: string | null
  panel: string
  analyte: string | null
  rule: string
  limitValue: string | null
  maxLoq: string | null
  uom: string
  requiredForRelease: boolean
  source: string
  version: number
  effectiveFrom: string | null
  createdAt: string | null
}
export const mapLabLimit = (r: Record<string, unknown>): LabLimitRow => ({
  id: str(r.id),
  cropId: strOrNull(r.crop_id),
  cropName: strOrNull(r.crop_name),
  panel: str(r.panel),
  analyte: strOrNull(r.analyte),
  rule: str(r.rule),
  limitValue: strOrNull(r.limit_value),
  maxLoq: strOrNull(r.max_loq),
  uom: str(r.uom),
  requiredForRelease: bool(r.required_for_release),
  source: str(r.source),
  version: num(r.version, 1),
  effectiveFrom: strOrNull(r.effective_from),
  createdAt: iso(r.created_at),
})
export function useLabLimits(f: PageParams & { cropId?: string; panel?: string; search?: string }) {
  return usePageQuery([...lk.limits, f], () => getPage('/lab-limits', f, mapLabLimit))
}
export function useCreateLabLimit() {
  return useConsoleMutation<Record<string, unknown>>({
    mutationFn: (body) => api.post('/lab-limits', body),
    invalidate: [lk.limits],
    success: 'Limit version added',
    errorTitle: 'Limit not saved',
  })
}
export function useDeleteLabLimit() {
  return useConsoleMutation<string>({
    mutationFn: (id) => api.delete(`/lab-limits/${id}`),
    invalidate: [lk.limits],
    success: 'Limit retired',
    errorTitle: 'Not retired',
  })
}

// ============================== SKUs (filter) ==============================

export interface SkuOption {
  id: string
  code: string
  name: string
}
export function useSkuOptions() {
  return useQuery({
    queryKey: lk.skus,
    staleTime: 10 * 60_000,
    queryFn: () =>
      getPage('/skus', { page: 1, limit: 200 }, (r): SkuOption => ({ id: str(r.id), code: str(r.code), name: str(r.name) })).then((p) => p.rows),
  })
}
